import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Building, ChevronDown, Eye, Plus, Save, Trash2,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Skeleton } from '@/components/insights/Skeleton';
import { ListingSummary, UnderlineTabs } from '@/components/Listing';
import { INDUSTRY_OPTIONS } from '@/config/industries';
import {
  industryConfigsService, IndustryStatus, IndustryTemplate, TemplateEntityType, TemplateFieldKind,
} from '@/services/industry-configs.service';
import {
  ENTITY_META, ENTITY_ORDER, KIND_META, STATUS_META, hasOptions, industryIcon, slugifyKey, timeAgo,
} from './industry-meta';
import { StatusSegmented } from './StatusChip';
import { Draft, FieldCard, QUICK_KINDS, Toggle, keyOf, newUid, toDrafts, toPayload } from './FieldEditor';

const orderedPayload = (drafts: Draft[]) =>
  ENTITY_ORDER.flatMap((e) => drafts.filter((f) => f.entityType === e).map((f, i) => toPayload(f, i)));

const snapshot = (drafts: Draft[], isActive: boolean) => JSON.stringify({ isActive, fields: orderedPayload(drafts) });

export default function IndustryConfigDetail() {
  const { industryKey = '' } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const backTo = `/industry-configs${(location.state as { from?: string } | null)?.from ?? ''}`;
  const option = INDUSTRY_OPTIONS.find((o) => o.value === industryKey);

  const [loaded, setLoaded] = useState(false);
  const [template, setTemplate] = useState<IndustryTemplate | null>(null);
  const [companies, setCompanies] = useState(0);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [baseline, setBaseline] = useState(snapshot([], true));
  const [tab, setTab] = useState<TemplateEntityType>('lead');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [focusUid, setFocusUid] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // The list endpoint is used (not GET :key) because an industry with no template
    // 404s there, and the global interceptor would toast that as an error.
    Promise.all([industryConfigsService.list(), industryConfigsService.usage().catch(() => ({} as Record<string, number>))])
      .then(([list, usage]) => {
        if (cancelled) return;
        const t = list.find((x) => x.industryKey === industryKey) ?? null;
        const d = toDrafts(t);
        setTemplate(t);
        setCompanies(usage[industryKey] ?? 0);
        setDrafts(d);
        setIsActive(t?.isActive ?? true);
        setBaseline(snapshot(d, t?.isActive ?? true));
        setTab(ENTITY_ORDER.find((e) => d.some((f) => f.entityType === e)) ?? 'lead');
      })
      .catch(() => { /* interceptor toasts */ })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [industryKey]);

  const dirty = loaded && snapshot(drafts, isActive) !== baseline;

  const issues = useMemo(() => {
    const out = new Map<string, string[]>();
    const seen = new Set<string>();
    for (const f of drafts) {
      const msgs: string[] = [];
      const key = keyOf(f);
      if (!f.name.trim()) msgs.push('Give the field a name.');
      if (!key) msgs.push('The key is empty.');
      else if (seen.has(`${f.entityType}:${key}`)) msgs.push(`Key “${key}” is already used by another ${ENTITY_META[f.entityType].singular.toLowerCase()} field.`);
      if (key) seen.add(`${f.entityType}:${key}`);
      if (hasOptions(f.kind) && !(f.options ?? []).length) msgs.push('Add at least one option.');
      if (msgs.length) out.set(f.uid, msgs);
    }
    return out;
  }, [drafts]);

  const tabFields = drafts.filter((f) => f.entityType === tab);
  const countFor = (e: TemplateEntityType) => drafts.filter((f) => f.entityType === e).length;
  const tabHasIssue = (e: TemplateEntityType) => drafts.some((f) => f.entityType === e && issues.has(f.uid));

  const patch = (uid: string, p: Partial<Draft>) =>
    setDrafts((ds) => ds.map((f) => {
      if (f.uid !== uid) return f;
      const next = { ...f, ...p };
      // Keep the key in step with the name until someone edits the key itself.
      if (p.name !== undefined && !f.keyTouched) next.key = slugifyKey(p.name);
      return next;
    }));

  const addField = (kind: TemplateFieldKind = 'text') => {
    const uid = newUid();
    setDrafts((ds) => [...ds, { uid, keyTouched: false, entityType: tab, name: '', key: '', kind, options: [], required: false }]);
    setFocusUid(uid);
    if (hasOptions(kind)) setExpanded((s) => new Set(s).add(uid));
  };

  const move = (uid: string, dir: -1 | 1) =>
    setDrafts((ds) => {
      const idx = ds.findIndex((f) => f.uid === uid);
      const entity = ds[idx]?.entityType;
      let j = idx + dir;
      while (j >= 0 && j < ds.length && ds[j].entityType !== entity) j += dir;
      if (idx < 0 || j < 0 || j >= ds.length) return ds;
      const next = [...ds];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  const duplicate = (uid: string) =>
    setDrafts((ds) => {
      const idx = ds.findIndex((f) => f.uid === uid);
      if (idx < 0) return ds;
      const src = ds[idx];
      const taken = new Set(ds.filter((f) => f.entityType === src.entityType).map(keyOf));
      let n = 2;
      while (taken.has(`${keyOf(src)}_${n}`)) n += 1;
      const copy: Draft = { ...src, uid: newUid(), name: `${src.name} (copy)`, key: `${keyOf(src)}_${n}`, keyTouched: true, options: [...(src.options ?? [])] };
      return [...ds.slice(0, idx + 1), copy, ...ds.slice(idx + 1)];
    });

  const remove = (uid: string) => setDrafts((ds) => ds.filter((f) => f.uid !== uid));

  const toggleExpanded = (uid: string) =>
    setExpanded((s) => { const n = new Set(s); if (n.has(uid)) n.delete(uid); else n.add(uid); return n; });

  const discard = () => {
    const d = toDrafts(template);
    setDrafts(d);
    setIsActive(template?.isActive ?? true);
    setBaseline(snapshot(d, template?.isActive ?? true));
    setShowErrors(false);
    setExpanded(new Set());
  };

  const save = useCallback(async () => {
    if (!option || saving) return;
    if (issues.size > 0) {
      setShowErrors(true);
      const first = drafts.find((f) => issues.has(f.uid));
      if (first) { setTab(first.entityType); setExpanded((s) => new Set(s).add(first.uid)); }
      toast.error(`Fix ${issues.size} field${issues.size === 1 ? '' : 's'} before saving`);
      return;
    }
    setSaving(true);
    try {
      const fields = orderedPayload(drafts);
      const saved = await industryConfigsService.upsert(industryKey, { label: option.label, fields, isActive });
      setTemplate(saved);
      setDrafts((ds) => ds.map((f) => ({ ...f, key: keyOf(f), keyTouched: true })));
      setBaseline(snapshot(drafts, isActive));
      setShowErrors(false);
      toast.success(`${option.label} saved — ${fields.length} field${fields.length === 1 ? '' : 's'}`);
    } catch { /* interceptor toasts */ } finally { setSaving(false); }
  }, [option, saving, issues, drafts, industryKey, isActive]);

  // Cmd/Ctrl+S saves; leaving with unsaved edits asks first.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') { e.preventDefault(); if (dirty) save(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dirty, save]);
  useEffect(() => {
    if (!dirty) return;
    const onUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  const goBack = () => {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    nav(backTo);
  };

  const changeStatus = async (next: IndustryStatus) => {
    if (!option) return;
    setStatusBusy(true);
    try {
      const saved = await industryConfigsService.setStatus(industryKey, next, option.label);
      setTemplate((t) => (t ? { ...t, status: saved.status, statusUpdatedAt: saved.statusUpdatedAt, updatedAt: saved.updatedAt } : saved));
      toast.success(`${option.label} marked ${STATUS_META[next].label.toLowerCase()}`);
    } catch { /* interceptor toasts */ } finally { setStatusBusy(false); }
  };

  const doDelete = async () => {
    setConfirmDelete(false);
    try {
      await industryConfigsService.remove(industryKey);
      toast.success(`${option?.label ?? industryKey} template deleted`);
      nav(backTo);
    } catch { /* interceptor toasts */ }
  };

  if (!option) {
    return (
      <>
        <Topbar title="Industry not found" breadcrumb="Catalog / Industry Configs" backTo="/industry-configs" />
        <div className="p-6">
          <div className="rounded-xl border border-dashed border-border bg-surface px-6 py-14 text-center">
            <p className="text-sm text-text-muted">There's no industry with the key <code className="font-mono">{industryKey}</code>.</p>
            <Link to="/industry-configs" className="mt-3 inline-block text-sm font-medium text-primary hover:underline">Back to all industries</Link>
          </div>
        </div>
      </>
    );
  }

  const Icon = industryIcon(option.value, option.group);
  const status: IndustryStatus = template?.status ?? 'not_started';

  return (
    <>
      <Topbar
        title={option.label}
        breadcrumb="Catalog / Industry Configs"
        description="Default fields every company assigned this industry starts with."
        onBack={goBack}
        right={(
          <Button onClick={save} disabled={!dirty} loading={saving} iconLeft={<Save size={16} />}>
            Save changes
          </Button>
        )}
      />
      <div className="mx-auto w-full max-w-[1680px] space-y-6 px-4 py-6 sm:px-7 sm:py-8">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-border bg-surface p-5 shadow-soft">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary-soft text-primary">
            <Icon size={24} strokeWidth={1.8} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold tracking-tight text-text-primary">{option.label}</h2>
              <span className="rounded-md bg-surface-alt px-1.5 py-0.5 font-mono text-[11px] text-text-muted">{option.value}</span>
              <span className="rounded-full bg-surface-alt px-2 py-0.5 text-xs font-medium text-text-secondary">{option.group}</span>
            </div>
            <p className="mt-1 max-w-2xl text-sm text-text-muted">
              Companies get their own copy when this industry is assigned — editing here never changes companies that already have it.
            </p>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <StatusSegmented status={status} onChange={changeStatus} busy={statusBusy} />
            <span className="text-2xs text-text-muted">
              {template?.statusUpdatedAt ? `Status changed ${timeAgo(template.statusUpdatedAt)}` : 'Build status'}
            </span>
          </div>
        </div>

        <ListingSummary
          items={[
            ...ENTITY_ORDER.map((e) => {
              const EIcon = ENTITY_META[e].icon;
              const n = countFor(e);
              return {
                label: `${ENTITY_META[e].singular} fields`,
                value: loaded ? n : '—',
                detail: n === 0 ? 'None yet' : `${drafts.filter((f) => f.entityType === e && f.required).length} required`,
                icon: <EIcon size={18} />,
              };
            }),
            { label: 'Companies', value: loaded ? companies : '—', detail: 'Assigned this industry', icon: <Building size={18} /> },
          ]}
        />

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          {/* ── Fields editor ─────────────────────────────────────────── */}
          <div className="min-w-0 rounded-2xl border border-border bg-surface shadow-soft">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 pt-4">
              <UnderlineTabs
                label="Record type"
                value={tab}
                onChange={setTab}
                tabs={ENTITY_ORDER.map((e) => ({
                  value: e,
                  label: ENTITY_META[e].label,
                  count: countFor(e),
                  dot: showErrors && tabHasIssue(e) ? 'bg-danger' : undefined,
                }))}
              />
              <span className="pb-3 text-xs text-text-muted">Reorder with the arrows · ⌘S saves</span>
            </div>

            <div className="space-y-3 p-5">
              {!loaded ? (
                Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-[76px] w-full rounded-xl" />)
              ) : tabFields.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border px-6 py-10 text-center">
                  <p className="text-sm font-semibold text-text-primary">No {ENTITY_META[tab].label.toLowerCase()} fields yet</p>
                  <p className="mx-auto mt-1 max-w-md text-sm text-text-muted">
                    Add the details a {option.label.toLowerCase()} business needs on every {ENTITY_META[tab].singular.toLowerCase()} — pick a type below to start.
                  </p>
                </div>
              ) : (
                tabFields.map((f, i) => (
                  <FieldCard
                    key={f.uid}
                    field={f}
                    first={i === 0}
                    last={i === tabFields.length - 1}
                    issues={showErrors ? issues.get(f.uid) : undefined}
                    expanded={expanded.has(f.uid)}
                    autoFocus={focusUid === f.uid}
                    onChange={(p) => patch(f.uid, p)}
                    onMove={(dir) => move(f.uid, dir)}
                    onToggle={() => toggleExpanded(f.uid)}
                    onDuplicate={() => duplicate(f.uid)}
                    onRemove={() => remove(f.uid)}
                  />
                ))
              )}

              {loaded && (
                <div className="rounded-xl border border-dashed border-border p-4">
                  <p className="mb-3 flex items-center gap-1.5 text-[13px] font-medium text-text-secondary">
                    <Plus size={15} /> Add a {ENTITY_META[tab].singular.toLowerCase()} field
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {QUICK_KINDS.map((k) => {
                      const KIcon = KIND_META[k].icon;
                      return (
                        <Button key={k} variant="secondary" size="sm" iconLeft={<KIcon size={14} className="text-primary" />} onClick={() => addField(k)}>
                          {KIND_META[k].label}
                        </Button>
                      );
                    })}
                  </div>
                  <p className="mt-3 text-2xs text-text-muted">You can change the type after adding it.</p>
                </div>
              )}
            </div>
          </div>

          {/* ── Side panel ────────────────────────────────────────────── */}
          <aside className="space-y-6 xl:sticky xl:top-24 xl:self-start">
            <FormPreview entity={tab} fields={tabFields} />

            <div className="rounded-2xl border border-border bg-surface p-5 shadow-soft">
              <h3 className="text-base font-semibold tracking-tight text-text-primary">Template</h3>
              <div className="mt-4 flex items-start justify-between gap-3">
                <span>
                  <span className="block text-sm font-medium text-text-primary">Copy to new companies</span>
                  <span className="block text-xs text-text-muted">Companies assigned {option.label} get these fields.</span>
                </span>
                <Toggle checked={isActive} onChange={setIsActive} label="Copy to new companies" />
              </div>
              <dl className="mt-4 space-y-2 border-t border-border pt-4 text-sm">
                <div className="flex justify-between"><dt className="text-text-muted">Total fields</dt><dd className="font-medium tabular-nums text-text-primary">{drafts.length}</dd></div>
                <div className="flex justify-between"><dt className="text-text-muted">Last saved</dt><dd className="text-text-primary">{template ? timeAgo(template.updatedAt) : 'Never'}</dd></div>
                <div className="flex justify-between"><dt className="text-text-muted">Companies assigned</dt><dd className="font-medium tabular-nums text-text-primary">{companies}</dd></div>
              </dl>
              {template && (
                <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(true)} iconLeft={<Trash2 size={14} />}
                  className="mt-4 w-full !text-danger hover:!bg-danger-soft">
                  Delete template
                </Button>
              )}
            </div>
          </aside>
        </div>

        {dirty && (
          <div className="sticky bottom-4 z-20 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-surface/95 px-5 py-3 shadow-elevated backdrop-blur">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-text-primary">
              <span className="h-2 w-2 rounded-full bg-warning" /> You have unsaved changes
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={discard}>Discard</Button>
              <Button size="sm" onClick={save} loading={saving}>Save changes</Button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete the ${option.label} template?`}
        message="All default fields and the build status for this industry are removed. Companies that already received these fields keep their copies."
        danger
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
}

function FormPreview({ entity, fields }: { entity: TemplateEntityType; fields: Draft[] }) {
  const ctl = 'flex h-10 w-full items-center rounded-lg border border-border bg-surface px-3.5 text-sm text-text-muted';
  return (
    <div className="rounded-2xl border border-border bg-surface shadow-soft">
      <div className="flex items-center gap-2 border-b border-border px-5 py-4">
        <Eye size={16} className="text-primary" />
        <h3 className="text-base font-semibold tracking-tight text-text-primary">Preview</h3>
        <span className="text-xs text-text-muted">New {ENTITY_META[entity].singular.toLowerCase()} in the CRM</span>
      </div>
      <div className="max-h-[520px] space-y-4 overflow-y-auto p-5">
        {fields.length === 0 ? (
          <p className="py-6 text-center text-xs text-text-muted">Fields you add appear here the way the company's team will see them.</p>
        ) : fields.map((f) => (
          <div key={f.uid}>
            <div className="mb-1.5 text-[13px] font-medium text-text-secondary">
              {f.name || <span className="italic text-text-muted">Untitled field</span>}
              {f.required && <span className="ml-0.5 text-primary">*</span>}
            </div>
            {f.kind === 'long_text' ? (
              <div className={clsx(ctl, 'h-16 items-start py-2')}>{f.placeholder}</div>
            ) : f.kind === 'boolean' ? (
              <span className="inline-flex items-center gap-2 text-xs text-text-muted">
                <span className={clsx('relative inline-flex h-4 w-7 items-center rounded-full', f.defaultValue === 'true' ? 'bg-primary' : 'bg-border')}>
                  <span className={clsx('inline-block h-3 w-3 rounded-full bg-white', f.defaultValue === 'true' ? 'translate-x-3.5' : 'translate-x-0.5')} />
                </span>
                {f.defaultValue === 'true' ? 'Yes' : 'No'}
              </span>
            ) : f.kind === 'multi_select' ? (
              <div className="flex flex-wrap gap-1">
                {(f.options ?? []).length
                  ? f.options!.map((o) => <span key={o} className="rounded-md border border-border px-2 py-0.5 text-[11px] text-text-muted">{o}</span>)
                  : <span className="text-[11px] italic text-text-muted">No options yet</span>}
              </div>
            ) : (
              <div className={clsx(ctl, 'justify-between')}>
                <span className="truncate">
                  {f.defaultValue || f.placeholder || (f.kind === 'dropdown' ? 'Select…' : KIND_META[f.kind].label)}
                </span>
                {f.kind === 'dropdown' && <ChevronDown size={14} />}
              </div>
            )}
            {f.helpText && <p className="mt-1 text-[11px] text-text-muted">{f.helpText}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
